import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, MessageCircle, PenLine, Pin, ArrowRight } from "lucide-react";
import api, { API } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export function useBlogSettings() {
  return useQuery({ queryKey: ["blog-settings"], queryFn: async () => (await api.get("/blog/settings")).data, staleTime: 60 * 1000 });
}

export function Avatar({ author, size = "h-8 w-8" }) {
  return author?.picture
    ? <img src={author.picture} alt="" className={`${size} rounded-full object-cover`} referrerPolicy="no-referrer" />
    : <span className={`${size} inline-flex items-center justify-center rounded-full bg-gold/20 font-mono text-xs font-bold uppercase text-gold`}>{(author?.name || "?").slice(0, 1)}</span>;
}

export function StatusPill({ status }) {
  const cls = { published: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300", rejected: "bg-red-500/15 text-red-700 dark:text-red-300", draft: "bg-muted text-muted-foreground" }[status] || "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`} data-testid={`post-status-${status}`}>{status === "pending" ? "awaiting review" : status}</span>;
}

export function PostCard({ post, to, showStatus = false }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-lg" data-testid={`post-card-${post.slug}`}>
      {post.cover_image_id ? (
        <Link to={to || `/blog/${post.slug}`} className="block aspect-[16/9] overflow-hidden bg-muted"><img src={`${API}/blog/images/${post.cover_image_id}`} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /></Link>
      ) : (
        <Link to={to || `/blog/${post.slug}`} className="grain flex aspect-[16/9] items-center justify-center bg-primary/90 px-6 text-center font-serif text-2xl italic leading-tight text-primary-foreground/90">“{post.title}”</Link>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-gold">{post.category}</span>
          {showStatus && <StatusPill status={post.status} />}
        </div>
        <h3 className="mt-2 font-serif text-xl font-semibold leading-tight text-foreground"><Link to={to || `/blog/${post.slug}`} className="hover:text-gold">{post.title}</Link></h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <Link to={`/blog/author/${post.author.user_id}`} className="flex min-w-0 items-center gap-2 hover:text-foreground"><Avatar author={post.author} size="h-6 w-6" /><span className="truncate">{post.author.name}</span></Link>
          <span className="flex shrink-0 items-center gap-3 font-mono text-[10px] uppercase tracking-wider">
            {post.published_at && <span>{fmtDate(post.published_at)}</span>}
            <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{post.amens}</span>
            <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{post.comments}</span>
          </span>
        </div>
      </div>
    </article>
  );
}

export function FeaturedPost({ post }) {
  return (
    <article className="group mt-8 grid overflow-hidden rounded-3xl border border-border bg-card lg:grid-cols-2" data-testid="blog-featured">
      <Link to={`/blog/${post.slug}`} className="block min-h-64 overflow-hidden bg-muted">
        {post.cover_image_id ? <img src={`${API}/blog/images/${post.cover_image_id}`} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="grain flex h-full min-h-64 items-center justify-center bg-primary/90 p-10 text-center font-serif text-3xl italic text-primary-foreground/90">“{post.title}”</div>}
      </Link>
      <div className="flex flex-col justify-center p-8 lg:p-12">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold"><Pin className="h-3.5 w-3.5" /> Featured · {post.category}</p>
        <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-foreground sm:text-4xl"><Link to={`/blog/${post.slug}`} className="hover:text-gold">{post.title}</Link></h2>
        <p className="mt-4 line-clamp-4 text-base leading-relaxed text-muted-foreground">{post.excerpt}</p>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <Link to={`/blog/author/${post.author.user_id}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><Avatar author={post.author} /><span>{post.author.name}</span>{post.published_at && <span>· {fmtDate(post.published_at)}</span>}</Link>
          <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="blog-featured-read">Read the post <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </article>
  );
}

/* Home-page teaser: pinned post first, then the newest. Renders nothing when the blog is off or empty. */
export function LatestFromBlog() {
  const { data } = useQuery({ queryKey: ["blog-latest"], queryFn: async () => (await api.get("/blog/latest", { params: { limit: 3 } })).data, staleTime: 60 * 1000 });
  const posts = data?.posts || [];
  if (!posts.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="home-blog-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">From the blog</p><h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Wisdom Is In a Question</h2></div>
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-gold" data-testid="home-blog-all">All posts <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
    </section>
  );
}

export function WriteButton({ className = "" }) {
  return <Link to="/blog/write" className={`inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${className}`} data-testid="blog-write-button"><PenLine className="h-4 w-4" /> Write a post</Link>;
}
